import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Ban, CheckCircle, Trash2 } from 'lucide-react';
import { adminGetUser, adminDisableUser, adminEnableUser, adminDeleteUser } from '../../lib/adminApi';
import type { AdminUserDetail, AdminUserRow, UserRole } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';

/*
 * Velaigal /admin/users/:id — single-user admin view.
 *
 * Read-only user/profile/activity blocks backed by adminGetUser, plus an
 * action bar that's the only interactive part of the page: Disable/Enable
 * (reversible, swaps by current state) and Delete (irreversible — cascades
 * server-side, see server/src/routes/admin.ts DELETE /api/admin/users/:id).
 * Both actions go through an inline two-step confirm (no window.confirm).
 *
 * Three guarded failures reach this page, not two. `forbidden_self` (acting
 * on your own account) and `last_admin` (would leave zero enabled admins)
 * arrive as human sentences that need nothing beyond the usual toast. Block E
 * added a third: `last_owner`, thrown by assertNoCompanyLosesItsLastOwner
 * (server/src/company/members.ts:57) from BOTH the disable and the delete
 * routes (server/src/routes/admin.ts:241 and :378) when the target is the last
 * active enabled owner of some company. That one gets a persistent remedy
 * below, because its sentence names a rule but no next step — and, unlike the
 * other two, no company id either.
 *
 * Precedence when more than one applies is server-side and fixed:
 * last_admin -> forbidden_self -> last_owner (CLAUDE.md, Admin surface).
 */

const ROLE_LABEL: Record<UserRole, string> = {
  jobseeker: 'Job seeker',
  hr: 'Recruiter',
  admin: 'Admin',
};

// Bookkeeping columns every profile row carries (userId is the join key,
// updatedAt is not user-facing) — hidden regardless of whether this is an
// hr_profiles or jobseeker_profiles row (see server/src/db/schema.ts).
const HIDDEN_PROFILE_KEYS = new Set(['userId', 'updatedAt']);

function humanizeKey(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function profileEntries(profile: object | null): Array<{ key: string; label: string; value: string }> {
  if (!profile) return [];
  return Object.entries(profile as Record<string, unknown>)
    .filter(([key, value]) => !HIDDEN_PROFILE_KEYS.has(key) && value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({ key, label: humanizeKey(key), value: String(value) }));
}

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this user.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">This user isn&apos;t available.</h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">It may have been deleted already.</p>
    <Link to="/admin/users" className="btn-primary">
      Back to users
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">Couldn&apos;t load this user.</h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

// tone 'cream' is for spinners inside dark-filled buttons (.btn-primary,
// .btn-danger); tone 'ink' is for spinners inside .btn-secondary, whose
// background is transparent/paper, not dark — cream-on-cream would be
// invisible there. Mirrors src/pages/HR/ApplicantDetail.tsx.
const InlineSpinner: React.FC<{ label: string; tone?: 'cream' | 'ink' }> = ({ label, tone = 'cream' }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={
        tone === 'ink'
          ? { borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }
          : { borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }
      }
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

interface ActionBarProps {
  user: AdminUserRow;
  onToggled: (disabledAt: string | null) => void;
  onDeleted: () => void;
}

/*
 * `last_owner` is the only failure on this page whose message states a rule without implying an
 * action, so it is the only one that earns a persistent block instead of a toast. The server
 * sends no `fields` and no company id with it (AppError(409, 'last_owner', ...) in
 * company/members.ts:57), so this copy must not pretend to name the company — saying which one
 * would be an invention.
 */
const LAST_OWNER_HINT =
  'This account is the last active owner of a company. Open that company under Companies, promote another recruiter to owner, then retry — the server does not say which company, so check the ones this user belongs to.';

const ActionBar: React.FC<ActionBarProps> = ({ user, onToggled, onDeleted }) => {
  const [confirmingToggle, setConfirmingToggle] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<{ message: string; hint: string } | null>(null);
  const isDisabled = !!user.disabledAt;

  // A toast is gone before the admin has finished reading a two-clause remedy, and this one
  // sends them to another page and back.
  const noteFailure = (err: unknown) => {
    if (err instanceof ApiError && err.code === 'last_owner') {
      setBlocked({ message: err.message, hint: LAST_OWNER_HINT });
    }
  };

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    setBlocked(null);
    try {
      if (isDisabled) {
        await adminEnableUser(user.id);
        onToggled(null);
        toast.success('User enabled.');
      } else {
        await adminDisableUser(user.id);
        onToggled(new Date().toISOString());
        toast.success('User disabled.');
      }
      setConfirmingToggle(false);
    } catch (err) {
      noteFailure(err);
      toast.error(err instanceof ApiError ? err.message : `Could not ${isDisabled ? 'enable' : 'disable'} this user.`);
      setConfirmingToggle(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setBlocked(null);
    try {
      await adminDeleteUser(user.id);
      onDeleted();
    } catch (err) {
      noteFailure(err);
      toast.error(err instanceof ApiError ? err.message : 'Could not delete this user.');
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-hairline">
      {blocked && (
        <div
          role="alert"
          className="border rounded-md p-3 mb-4"
          style={{ borderColor: 'var(--color-error)', backgroundColor: 'rgba(153, 27, 27, 0.08)' }}
        >
          <p className="text-sm font-medium" style={{ color: 'var(--color-error)' }}>
            {blocked.message}
          </p>
          <p className="text-xs text-ink-secondary mt-1 leading-relaxed">{blocked.hint}</p>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {confirmingToggle ? (
          <>
            <button type="button" className="btn-secondary" onClick={() => void handleToggle()} disabled={busy}>
              {busy ? (
                <InlineSpinner label={isDisabled ? 'Enabling…' : 'Disabling…'} tone="ink" />
              ) : (
                `Confirm ${isDisabled ? 'enable' : 'disable'}`
              )}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setConfirmingToggle(false)} disabled={busy}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn-secondary" onClick={() => setConfirmingToggle(true)} disabled={busy}>
            {isDisabled ? (
              <CheckCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
            ) : (
              <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
            {isDisabled ? 'Enable user' : 'Disable user'}
          </button>
        )}

        {confirmingDelete ? (
          <>
            <button type="button" className="btn-danger" onClick={() => void handleDelete()} disabled={busy}>
              {busy ? <InlineSpinner label="Deleting…" /> : 'Confirm delete'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setConfirmingDelete(false)} disabled={busy}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn-danger" onClick={() => setConfirmingDelete(true)} disabled={busy}>
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            Delete user
          </button>
        )}
      </div>
      {(confirmingToggle || confirmingDelete) && (
        <p className="mt-3 text-xs text-ink-tertiary">
          {confirmingDelete
            ? 'This permanently deletes the account, their resumes, applications, and submissions — it cannot be undone.'
            : 'This changes what the user can access immediately.'}
        </p>
      )}
    </div>
  );
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load-by-id effect in ApplicantDetail.tsx.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await adminGetUser(id);
        if (cancelled) return;
        setDetail(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  const handleToggled = (disabledAt: string | null) => {
    setDetail((prev) => (prev ? { ...prev, user: { ...prev.user, disabledAt } } : prev));
  };

  const handleDeleted = () => {
    toast.success('User deleted.');
    navigate('/admin/users');
  };

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (!detail) return null;

  const { user, profile, activity } = detail;
  const entries = profileEntries(profile);

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">{ROLE_LABEL[user.role]}</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-1">{user.name}</h1>
      <p className="text-sm text-ink-secondary mb-8">{user.email}</p>

      <div className="card mb-6">
        <span className="label-editorial block mb-4">Account</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className="label-editorial block mb-1">Role</span>
            <p className="text-sm text-ink">{ROLE_LABEL[user.role]}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Status</span>
            <p className="text-sm text-ink">{user.disabledAt ? 'Disabled' : 'Active'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Email verified</span>
            <p className="text-sm text-ink">{user.emailVerifiedAt ? `Yes — ${formatRelativeDate(user.emailVerifiedAt)}` : 'No'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Joined</span>
            <p className="text-sm text-ink">{formatRelativeDate(user.createdAt)}</p>
          </div>
          {user.disabledAt && (
            <div>
              <span className="label-editorial block mb-1">Disabled</span>
              <p className="text-sm text-ink">{formatRelativeDate(user.disabledAt)}</p>
            </div>
          )}
        </div>
      </div>

      {entries.length > 0 && (
        <div className="card mb-6">
          <span className="label-editorial block mb-4">Profile</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {entries.map((entry) => (
              <div key={entry.key}>
                <span className="label-editorial block mb-1">{entry.label}</span>
                <p className="text-sm text-ink whitespace-pre-line">{entry.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <span className="label-editorial block mb-4">Activity</span>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <span className="label-editorial block mb-1">Jobs</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.jobs}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Applications</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.applications}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Resumes</span>
            <p className="font-display text-2xl font-medium text-ink">{activity.resumes}</p>
          </div>
        </div>
      </div>

      <ActionBar user={user} onToggled={handleToggled} onDeleted={handleDeleted} />
    </div>
  );
};

export default UserDetail;
